import { DocumentType } from '~/constants/documentType';
import type { CustomerRequestParams } from '~/services/modules/quotes/types';

/** `2000-05-15T00:00:00.000Z` (ou `2000-05-15`) → `15/05/2000`. Vazio se não der. */
export function isoToBirthDateMask(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00.000Z` : iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getUTCFullYear()}`;
}

/** `15/05/2000` → `2000-05-15T00:00:00.000Z`. Vazio se a data não for completa/válida. */
export function birthDateMaskToIso(masked: string | null | undefined): string {
  if (!masked || masked.length !== 10) return '';
  const [day, month, year] = masked.split('/').map(Number);
  if (!day || !month || !year) return '';
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCDate() !== day || date.getUTCMonth() !== month - 1 || date.getUTCFullYear() !== year) return '';
  return date.toISOString();
}

export type HolderFormValues = {
  holderName: string;
  holderDocument: string;
  holderBirthDate: string;
  holderPhone: string;
  holderEmail: string;
};

/**
 * Monta o corpo de `PUT /v2/Quotes/{quoteHash}/customer`
 * (`UpdateQuoteCustomerViewModel`) a partir do bloco «Dados do titular».
 *
 * O schema da API **não** tem `gender`: o género só é aceite na criação da
 * cotação (`CreateQuoteCustomerViewModel`), por isso não é reenviado aqui.
 */
export function buildQuoteCustomerPayload(
  values: HolderFormValues,
  extras: { socialName?: string | null; organizationId?: string | null; customerId?: string | null } = {}
): CustomerRequestParams {
  return {
    name: values.holderName.trim(),
    email: values.holderEmail.trim(),
    mobile_phone: values.holderPhone.replace(/\D/g, ''),
    document: values.holderDocument.replace(/\D/g, ''),
    document_type: DocumentType.CPF,
    birthDate: birthDateMaskToIso(values.holderBirthDate),
    ...(extras.socialName ? { social_name: extras.socialName } : {}),
    ...(extras.customerId ? { customer_id: extras.customerId } : {}),
    ...(extras.organizationId ? { organization_id: extras.organizationId } : {})
  };
}

const HOLDER_FIELDS: Array<keyof HolderFormValues> = [
  'holderName',
  'holderDocument',
  'holderBirthDate',
  'holderPhone',
  'holderEmail'
];

/**
 * Valores para o `reset()` depois do Pix da adesão: limpa os dados do cartão, mas mantém «Dados do titular».
 * Antes o `reset()` sem argumentos apagava o titular e o preenchimento automático não voltava a correr,
 * obrigando a redigitar os 5 campos na mensalidade (e o caminho «reusar cartão» mandava o titular vazio).
 */
export function holderValuesToKeep(values: Partial<Record<string, unknown>>): HolderFormValues {
  return HOLDER_FIELDS.reduce((acc, field) => {
    const value = values[field];
    acc[field] = typeof value === 'string' ? value : '';
    return acc;
  }, {} as HolderFormValues);
}

/**
 * Grava o titular antes de cobrar. Se o `PUT Quotes/{hash}/customer` falhar, o pagamento **não** segue:
 * a cobrança sairia com o titular antigo. O erro sobe para o `catch` do submit mostrar o toast da API.
 */
export async function updateHolderBeforePayment(
  put: () => Promise<unknown>,
  onError: (error: unknown) => void
): Promise<void> {
  try {
    await put();
  } catch (error) {
    onError(error);
    throw error;
  }
}
